<script>
  import { External, M } from '../../external';
  import { onMount } from 'svelte';
  import { slide, fade } from 'svelte/transition';
  import TextEditor from '../../Common/TextEditor.svelte';

  export let contact;

  const editor = {
    placeholderText: M.CONTACT_NOTE_ADD.charAt(0).toUpperCase() + M.CONTACT_NOTE_ADD.slice(1).toLowerCase() + '...',
    isEditing: false,
    data: '',
    meta: {
      forceFixedPosition: false,
      addExtraButtons: false,
      addImageButton: false,
      addMarketingTags: false,
      useEmailFontsOnly: false,
      focused: true
    },
    reset: () => {
      editor.data = '';
      editor.isEditing = false;
    }
  };

  const getUserInitials = (name, email) => {
    const userData = External.Tools.getColorAndInitialsFromNameAndEmail(name, email);

    return userData.initials;
  };

  const getUserColor = (name, email) => {
    if (!email) {
      // This will make sure that migrated contacts will have same color
      email = 'migrated';
    }
    const userData = External.Tools.getColorAndInitialsFromNameAndEmail(name, email);

    return userData.color;
  };

  const notes = {
    loading: true,
    data: [],
    fetch: () => {
      notes.loading = true;
      External.Tools.makeWebsiteRequest({
        action: 'getContactNotes',
        data: {
          id: contact.data.id
        },
        onOk: function(response) {
          notes.data = response.data;
          notes.loading = false;
        }
      });
    },
    add: (value, onSuccess) => {
      if (!value || value.length === 0) {
        return;
      }
      // Remove all HTML tags and whitespace characters, because <p><br></p> is still considered as a valid value
      const cleanValue = value.replace(/<\/?[^>]+(>|$)/g, '').trim();
      if (cleanValue.length === 0) {
        return;
      }
      External.Tools.makeWebsiteRequest({
        action: 'addContactNote',
        method: 'post',
        data: {
          id: contact.data.id,
          value: value
        },
        onOk: function(response) {
          notes.data = [response.data, ...notes.data];
          if (onSuccess) {
            onSuccess();
          }
        }
      });
    },
    update: (note, newBody, onSuccess) => {
      External.Tools.makeWebsiteRequest({
        action: 'updateContactNote',
        method: 'post',
        data: {
          id: contact.data.id,
          note: JSON.stringify({ ...note, body: newBody })
        },
        onOk: function(response) {
          const updatedNote = response.data;
          if (onSuccess) {
            onSuccess(updatedNote);
          }
        }
      });
    },
    delete: (note) => {
      External.Tools.makeWebsiteRequest({
        action: 'deleteContactNote',
        method: 'post',
        data: {
          id: contact.data.id,
          note: JSON.stringify(note)
        },
        onOk: function() {
          notes.data = notes.data.filter((n) => n.timestamp !== note.timestamp);
        }
      });
    }
  };

  const shouldShowActions = (note) => {
    if (note.isEditing) {
      return false;
    }
    return note.user.email === External.userEmail;
  };

  let sliding = false;

  onMount(() => {
    notes.fetch();
  });
</script>

<div class="col20 f-left profile-notes-wrapper mt-15">
  <div class="communication-row col20 f-left flex flex-row">
    <div class="kanban-admin flex-none" style="background-color: {getUserColor(External.userName, External.userEmail)}">
      {getUserInitials(External.userName, External.userEmail)}
    </div>
    <div class="communication-user communication-compose">
      <div class="communication-wrapper">
        <div class="communication-message flex flex-col" on:click={() => (editor.isEditing = true)}>
          {#if !editor.isEditing}
            <div class="communication-header f-left flex align-center">
              <div class="communication-body f-left" style="line-height:1;">{editor.placeholderText}</div>
            </div>
          {:else}
            <div
              in:slide
              class:min-h-100px={!sliding}
              on:introstart={() => (sliding = true)}
              on:introend={() => (sliding = false)}>
              <TextEditor
                data={editor.data}
                meta={editor.meta}
                onChange={(changed) => {
                  editor.data = changed;
                }}
                isMinimalistic={false}
                emailEditor={false} />
            </div>
          {/if}
        </div>
      </div>
      {#if editor.isEditing}
        <a
          class="middle-button gray rounded-effect ml-5 mt-5 f-right flex-none"
          on:click={() => {
            editor.isEditing = false;
          }}>{M.CANCEL}</a>
        <a
          class="middle-button blue rounded-effect ml-5 mt-5 f-right flex-none"
          on:click={() => {
            notes.add(editor.data, editor.reset);
          }}>{M.SAVE}</a>
      {/if}
    </div>
  </div>
  {#if notes.loading}
    <div class="small-centered-loading-icon"></div>
  {:else}
    {#each notes.data as note}
      <div class="communication-row col20 f-left flex flex-row">
        <div class="kanban-admin flex-none" style="background-color: {getUserColor(note.user.name, note.user.email)}">
          {getUserInitials(note.user.name, note.user.email)}
        </div>
        <div class="communication-user communication-note">
          {#if note.isEditing}
            <div in:fade={{ duration: 200 }}>
              <div class="communication-wrapper w-100 f-left">
                <div class="communication-user communication-compose max-w-unset">
                  <div class="communication-message flex flex-col">
                    <TextEditor
                      data={note.draft}
                      meta={editor.meta}
                      onChange={(changed) => {
                        note.draft = changed;
                      }}
                      isMinimalistic={false}
                      emailEditor={false} />
                  </div>
                </div>
              </div>
              <a
                class="middle-button gray rounded-effect ml-5 mt-5 f-right flex-none"
                on:click={() => {
                  note.isEditing = false;
                }}>{M.CANCEL}</a>
              <a
                class="middle-button blue rounded-effect ml-5 mt-5 f-right flex-none"
                on:click={() => {
                  notes.update(note, note.draft, (updatedNote) => {
                    note.body = updatedNote.body;
                    note.updatedAt = updatedNote.updatedAt;
                    note.isEditing = false;
                  });
                }}>{M.SAVE}</a>
            </div>
          {:else}
            <div class="communication-wrapper f-left">
              <div class="communication-message flex flex-col">
                <div class="communication-body note-editor f-left">{@html note.body}</div>
              </div>
            </div>
          {/if}
          <div class="communication-timestamp">
            {#if note.user.name}
              <b>{note.user.name}</b> - {External.Tools.dateToString(new Date(note.timestamp), true)}
              {#if note.updatedAt}
                - <b title={External.Tools.dateToString(new Date(note.updatedAt), true)}>{M.EDITED}</b>
              {/if}
            {/if}
            {#if shouldShowActions(note)}
              <div class="action-wrapper">
                <a
                  class="pay-status light-gray-bg text-center"
                  on:click={() => {
                    note.isEditing = true;
                    note.draft = note.body;
                  }}>
                  {M.EDIT}
                </a>
                <a
                  class="pay-status light-gray-bg text-center"
                  on:click={() => {
                    External.Tools.prompt(M.DELETE, M.DEL_CONFIRM, () => {
                      notes.delete(note);
                    });
                  }}>
                  {M.DELETE}
                </a>
              </div>
            {/if}
          </div>
        </div>
      </div>
    {/each}
  {/if}
</div>

<style>
  .w-100 {
    width: 100%;
  }

  .min-h-100px {
    min-height: 100px;
  }

  .max-w-unset {
    max-width: unset;
  }

  .action-wrapper {
    display: flex;
    flex-direction: row;
    align-items: end;
  }

  .action-wrapper a {
    margin-left: 4px;
  }
</style>
